import type { PluginWithOptions } from 'markdown-it';
import type { MarkdownParser, MarkdownSerializer } from 'prosemirror-markdown';
import type { Attrs, Node as ProsemirrorNode, Schema } from 'prosemirror-model';
import type { EditorState, Transaction } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type React from 'react';
import type { baseDictionary } from '.';
import type { NodeViewRenderer } from './components/NodeViewRenderer';
import type Extension from './lib/Extension';
import type { CommandFactory } from './lib/Extension';
import type ExtensionManager from './lib/ExtensionManager';
import type Mark from './marks/Mark';
import type Node from './nodes/Node';

export enum ToastType {
	Error = 'error',
	Info = 'info',
}

export type MenuItem = {
	icon?: typeof React.Component | React.FC<any>;
	name?: string;
	title?: string;
	shortcutKeys?: string[];
	keywords?: string;
	tooltip?: string;
	defaultHidden?: boolean;
	attrs?: Attrs;
	visible?: boolean;
	active?: (state: EditorState) => boolean;
	menuOptions?: MenuItem[];
};

export type EmbedDescriptor = MenuItem & {
	matcher: (url: string) => boolean | [] | RegExpMatchArray;
	component: typeof React.Component | React.FC<any>;
};

export interface SecodaEditorState {
	id?: string;
	singleLineEditor: boolean;
	isTemplate: boolean;
	readOnly: boolean;
	disableExtensions: Array<string>;
	schema: Schema;
	view: EditorView;
	uploadFile?: (file: File, isImage: boolean) => Promise<string>;
	onFileUploadStart?: () => void;
	onFileUploadStop?: () => void;
	onShowToast?: (message: string, code: ToastType | string) => void;
	embeds?: EmbedDescriptor[];
	disableResourceLinking?: boolean;
}

export type EditorDictionary = typeof baseDictionary;

export interface MarkdownPluginOptions {
	disabledExtensions: Array<string>;
}

export type MarkdownPlugin = PluginWithOptions<MarkdownPluginOptions>;

export type SecodaEditorComponentProps = {
	node: ProsemirrorNode;
	theme: unknown;
	isSelected: boolean;
	isEditable: boolean;
	getPos: () => number;
	view: EditorView;
};

export type Dispatch = (tr: Transaction) => void;

// paragraph cannot be included in this list! it's the node to render any plain text
export type ExtensionsNames =
	| 'strong'
	| 'code_inline'
	| 'highlight'
	| 'em'
	| 'link'
	| 'placeholder'
	| 'strikethrough'
	| 'underline'
	| 'blockquote'
	| 'bullet_list'
	| 'checkbox_item'
	| 'checkbox_list'
	| 'code_block'
	| 'code_fence'
	| 'embed'
	| 'br'
	| 'heading'
	| 'hr'
	| 'image'
	| 'list_item'
	| 'container_notice'
	| 'ordered_list'
	| 'table'
	| 'td'
	| 'th'
	| 'tr'
	| 'emoji'
	| 'emojimenu'
	| 'holy'
	| 'query_block'
	| 'chart_block'
	| 'ai_block'
	| 'page'
	| 'attachment'
	| 'comment'
	| 'blockmenu'
	| 'toc'
	| 'ai_summary'
	| 'resource_link'
	| 'trailing_node'
	| 'find-and-replace';

export type SecodaEditorProps = {
	id?: string;
	template?: boolean;
	extensions: Array<Extension | Node | Mark>;
	handleChange?: (value?: string) => void;
	disableExtensions?: ExtensionsNames[];
	disableInputExtensions?: ExtensionsNames[];
	embeds?: EmbedDescriptor[];
	readOnly?: boolean;
	scrollTo?: string;
	autoFocus?: boolean;
	onShowToast?: (message: string, code: ToastType | string) => void;
	onClickLink?: (
		href: string,
		event: MouseEvent,
		forceNewTab?: boolean
	) => void;
	focusedCommentID?: string;
	onCreatePlaceholderComment?: (selectedText: string) => void;
	onClickComment?: (commentID: string) => void;
	onTrackEvent?: (
		eventName: string,
		properties?: Record<string, string>
	) => void;
	uploadFile?: (file: File, isImage: boolean) => Promise<string>;
	onFileUploadStart?: () => void;
	onFileUploadStop?: () => void;
	singleLineEditor?: boolean;
	disableResourceLinking?: boolean;
	onSave?: ({ done }: { done: boolean }) => void;
	onCancel?: () => void;
};

export enum Toolbar {
	Selection = 'selection',
	Link = 'link',
	Mention = 'mention',
	Emoji = 'emoji',
	BlockMenu = 'block-menu',
	Placeholder = 'placeholder',
	Comment = 'comment',
	FindAndReplace = 'find-and-replace',
	IntegrationPicker = 'integration-picker',
}

export interface ISecodaEditor {
	extensions: ExtensionManager;
	schema: Schema;
	view?: EditorView;
	options: SecodaEditorProps & Record<string, unknown>;
	pasteParser: MarkdownParser;
	serializer: MarkdownSerializer;
	commands: Record<string, CommandFactory>;
	renderers: Set<NodeViewRenderer<SecodaEditorComponentProps>>;
	openedToolbar: Toolbar | null;
	closeToolbars: (toolbar?: Toolbar) => void;
	openToolbar: (toolbar: Toolbar, search?: string) => void;
	setBlockMenuSearch: (search: string) => void;
	blockMenuSearch?: string;
	scrollToAnchor: (hash: string) => void;
}
